import { TemplateFolderSelectModal } from "@/pages/talent/templates/TemplateFolderSelectModal.tsx";
import { emojiDefaults } from "@/components/emoji/emoji.type.ts";
import { EmployeeAvatar } from "@/components/employee/EmployeeAvatar.tsx";
import { EmployeeHoverCard } from "@/components/employee/EmployeeHoverCard.tsx";
import EmptyStateNothingFound from "@/components/empty-state/EmptyStateNothingFound.tsx";
import { filtersLibrary } from "@/components/filter/filter.types.ts";
import { useAppFilter } from "@/components/filter/use-filter.hook.tsx";
import {
  Avatar,
  ConfirmationModal,
  EmptyStateBase,
  Page,
  PaginationBlock,
  useModals,
  Button,
  DropdownMenu,
  DropdownMenuItem,
  FontAwesomeIcon,
  Stack,
  Table,
  TableCell,
  Text,
} from "@skailer-com/uikit";
import { Search } from "@/components/search/Search.tsx";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import { useTemplateEditPage } from "@/pages/talent/surveys/templates/edit/pages/survey-template-edit-pages.tsx";
import { SurveyTemplateEditPageStep } from "@/pages/talent/surveys/templates/edit/survey-template-edit.ts";
import {
  useSurveyTemplateDelete,
  useSurveyTemplateMove,
  useSurveyTemplateDuplicate,
  useSurveyTemplateSave,
  useSurveyTemplateSearch,
} from "@/queries/surveys/use-survey-templates.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { AnalyticReportTypes } from "@/types/analytics/analytics.types.ts";
import { ColumnSizes } from "@/types/common.types.ts";
import {
  ApiFormTemplate,
  ApiFormTemplateFilter,
  ApiFormTemplateType,
} from "@/types/forms/form-template.types.ts";
import { ApiPermission } from "@/types/role.types.ts";
import { ApiFeatureType } from "@/types/subscription.types.ts";
import { ColumnDef, Row } from "@tanstack/react-table";
import { round } from "lodash";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { useDebounce, useEffectOnce } from "react-use";

export const SurveyTemplatesTable = ({ folderId }: { folderId?: number }) => {
  const { t } = useTranslation();
  const { openModal } = useModals();
  const { navigateToStep } = useTemplateEditPage();
  const { canIEdit, isFeatureEnabled } = usePermissions();
  const { renderEmployeeName } = useFormatter();

  const [searchParams, setSearchParams] = useSearchParams();
  const library = filtersLibrary();
  const [searchTerm, setSearchTerm] = useState(searchParams.get("search") ?? "");

  const canManageTemplates = canIEdit(ApiPermission.survey_templates);
  const canDuplicateToPerformanceReviews =
    isFeatureEnabled(ApiFeatureType.talent_performance_reviews) &&
    canIEdit(ApiPermission.performance_reviews_module) &&
    canIEdit(ApiPermission.performance_review_templates);
  const templateSave = useSurveyTemplateSave();
  const templateDuplicate = useSurveyTemplateDuplicate();
  const templateDelete = useSurveyTemplateDelete();
  const templateMove = useSurveyTemplateMove();

  const {
    filter: { filter: filter },
  } = useAppFilter<ApiFormTemplateFilter>({
    filters: [library.page_number, library.text],
    source: AnalyticReportTypes.formTemplate,
  });

  const {
    data: templates,
    isLoading,
    isFetching,
  } = useSurveyTemplateSearch({ ...filter, folder_id: folderId });

  useDebounce(
    () => {
      setSearchParams((searchParams) => {
        if ((searchParams.get("search") ?? "") !== searchTerm) searchParams.delete("page");
        if (searchTerm) searchParams.set("search", searchTerm);
        else searchParams.delete("search");
        return searchParams;
      });
    },
    300,
    [searchTerm]
  );

  const handleCreate = useCallback(async () => {
    try {
      const template = await templateSave.mutateAsync({
        id: undefined,
        payload: {
          title: "",
          folder_id: folderId,
        },
      });

      analyticsService.trackEvent(analyticEvents.surveyTemplates.created, {
        [analyticProperties.id]: template.id,
      });

      navigateToStep(template.id, SurveyTemplateEditPageStep.details);
    } catch {
      return;
    }
  }, [folderId, navigateToStep, templateSave]);

  const handleDuplicate = useCallback(
    (template: ApiFormTemplate, targetType?: ApiFormTemplateType) => {
      const duplicate = async (folderId?: number) => {
        await templateDuplicate.mutateAsync({
          id: template.id,
          payload: targetType ? { target_type: targetType, folder_id: folderId } : undefined,
        });
        analyticsService.trackEvent(analyticEvents.surveyTemplates.duplicated, {
          [analyticProperties.id]: template.id,
        });
      };

      if (targetType) {
        openModal(TemplateFolderSelectModal, {
          module: "performance-reviews",
          modalTitle: t("surveys:duplicate_template_in_performance_reviews"),
          text: t("surveys:duplicate_template_in_performance_reviews_confirm"),
          acceptTitle: t("common:duplicate"),
          onConfirm: duplicate,
        });
        return;
      }

      openModal(ConfirmationModal, {
        question: t("surveys:duplicate_template"),
        text: t("surveys:duplicate_template_confirm"),
        acceptTitle: t("common:duplicate"),
        onAccept: async () => {
          try {
            await duplicate();
          } catch {
            return;
          }
        },
      });
    },
    [openModal, t, templateDuplicate]
  );

  const handleDelete = useCallback((template: ApiFormTemplate) => {
    openModal(ConfirmationModal, {
      question: t("surveys:delete_template"),
      text: t("common:delete_confirm"),
      acceptTitle: t("surveys:delete_template"),
      onAccept: async function () {
        try {
          await templateDelete.mutateAsync({ id: template.id });

          analyticsService.trackEvent(analyticEvents.surveyTemplates.deleted, {
            [analyticProperties.id]: template.id,
          });
        } catch {
          return;
        }
      },
    });
  }, []);

  const columns = useMemo<ColumnDef<ApiFormTemplate>[]>(
    () => [
      {
        header: t("common:title"),
        accessorKey: "title",
        ...ColumnSizes.standard,
        cell: (row) => (
          <Stack
            direction={"horizontal"}
            items={"center"}
            gap={"smd"}
            className={"overflow-hidden text-ellipsis"}
          >
            <Avatar
              size={"md"}
              emoji={row.row.original.emoji || emojiDefaults.surveyTemplate.default}
            />
            <TableCell>{row.row.original.title}</TableCell>
          </Stack>
        ),
      },
      {
        header: t("common:questions"),
        accessorKey: "questions",
        ...ColumnSizes.status,
        cell: (row) => (
          <TableCell variant={"secondary"}>
            {t("plurals:question_count", { count: row.row.original.total_questions })}
          </TableCell>
        ),
      },
      {
        header: t("common:time_to_complete"),
        accessorKey: "time_to_complete",
        ...ColumnSizes.status,
        cell: (row) => (
          <TableCell variant={"secondary"}>
            {t("plurals:plural_minutes", {
              count: round(row.row.original.total_estimated_time_seconds / 60),
            })}
          </TableCell>
        ),
      },
      {
        header: t("common:author"),
        accessorKey: "author",
        ...ColumnSizes.standard,
        meta: { disableRowClick: true },
        cell: (row) => (
          <EmployeeHoverCard employee={row.row.original.author}>
            <Stack direction={"horizontal"} gap={"sm"} items={"center"}>
              <EmployeeAvatar employee={row.row.original.author} size={"sm"} />
              <Text>{renderEmployeeName(row.row.original.author)}</Text>
            </Stack>
          </EmployeeHoverCard>
        ),
      },
    ],
    [templates]
  );

  const renderRowAction = useCallback(
    (row: Row<ApiFormTemplate>) => {
      if (!canManageTemplates) return null;
      const template = row.original;
      return (
        <DropdownMenu>
          <DropdownMenuItem
            onClick={() => navigateToStep(template.id, SurveyTemplateEditPageStep.details)}
            leftIcon={<FontAwesomeIcon icon="fa-light fa-pen" />}
            title={t("common:edit")}
          />
          <DropdownMenuItem
            onClick={() => handleDuplicate(template)}
            leftIcon={<FontAwesomeIcon icon="fa-light fa-copy" />}
            title={t("surveys:duplicate_template") + "..."}
          />
          {canDuplicateToPerformanceReviews && (
            <DropdownMenuItem
              onClick={() => handleDuplicate(template, ApiFormTemplateType.performance_review)}
              leftIcon={<FontAwesomeIcon icon="fa-light fa-copy" />}
              title={`${t("surveys:duplicate_template_in_performance_reviews")}...`}
            />
          )}
          <DropdownMenuItem
            title={t("common:move_to") + "..."}
            leftIcon={<FontAwesomeIcon icon="fa-light fa-folder-arrow-up" />}
            onClick={() =>
              openModal(TemplateFolderSelectModal, {
                module: "surveys",
                modalTitle: t("common:move_to"),
                acceptTitle: t("common:save"),
                currentFolderId: template.folder?.id,
                onConfirm: async (folderId) => {
                  await templateMove.mutateAsync({
                    id: template.id,
                    payload: { folder_id: folderId },
                  });
                },
              })
            }
          />
          <DropdownMenuItem
            onClick={() => handleDelete(template)}
            leftIcon={<FontAwesomeIcon icon="fa-light fa-trash" />}
            variant={"menuDanger"}
            title={t("surveys:delete_template") + "..."}
          />
        </DropdownMenu>
      );
    },
    [
      canManageTemplates,
      canDuplicateToPerformanceReviews,
      navigateToStep,
      handleDuplicate,
      handleDelete,
      templateMove,
      openModal,
      t,
    ]
  );

  useEffectOnce(() => {
    analyticsService.trackEvent(analyticEvents.surveyTemplates.viewed);
  });

  return (
    <Page.StickyLayout
      toolbar={
        <Stack direction={"horizontal"}>
          <Search
            value={searchTerm}
            onInput={(e) => {
              setSearchTerm(e.currentTarget.value);
            }}
            type={"text"}
            placeholder={t("common:search_by_name")}
            className={"w-96"}
          />
        </Stack>
      }
      footer={
        <PaginationBlock
          metadata={templates?.metadata}
          onPageChange={(page) => {
            setSearchParams((searchParams) => {
              searchParams.set("page", page.toString());
              return searchParams;
            });
          }}
        />
      }
    >
      <Table
        stickyHeader={true}
        renderRowAction={renderRowAction}
        onRowClick={(row) =>
          canManageTemplates && navigateToStep(row.original.id, SurveyTemplateEditPageStep.details)
        }
        data={templates?.items || []}
        columns={columns}
        showSkeleton={isLoading}
        isFetching={isFetching}
        showNotFoundError={filter.text != undefined && templates?.metadata.total_items == 0}
        notFoundComponent={<EmptyStateNothingFound />}
        emptyStateComponent={
          <EmptyStateBase
            emoji={emojiDefaults.surveyTemplate.default}
            title={t("surveys:empty_state_survey_templates_title")}
            description={t("surveys:empty_state_survey_templates_description")}
            primaryButton={
              canManageTemplates && (
                <Button
                  onClick={() => handleCreate()}
                  size={"sm"}
                  variant={"secondary"}
                  leftIcon={<FontAwesomeIcon icon={"fa-light fa-plus"} />}
                >
                  {t("surveys:create_template")}
                </Button>
              )
            }
          />
        }
      />
    </Page.StickyLayout>
  );
};
